import React, { useState } from 'react';

// Define styles using TypeScript interfaces
interface Styles {
  container: React.CSSProperties;
  title: React.CSSProperties;
  inputContainer: React.CSSProperties;
  input: React.CSSProperties;
  button: React.CSSProperties;
  buttonDisabled: React.CSSProperties;
  imageContainer: React.CSSProperties;
  image: React.CSSProperties;
  loadingIndicator: React.CSSProperties;
  pageContainer: React.CSSProperties;
}

const styles: Styles = {
  pageContainer: {
    width: '100%',
    height: '100vh',
    overflowY: 'auto',
    padding: '2rem 5rem',
  },
  container: {
    maxWidth: '80%',
    minHeight: '100%',
    margin: '0 auto',
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  inputContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2rem',
  },
  input: {
    padding: '12px 16px',
    fontSize: '1rem',
    borderRadius: '8px',
    border: '1px solid #ddd',
    width: '100%',
    boxSizing: 'border-box',
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#4a90e2',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    width: '100%',
  },
  buttonDisabled: {
    backgroundColor: '#a0c4f0',
    cursor: 'not-allowed',
  },
  imageContainer: {
    marginTop: '2rem',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
  },
  image: {
    maxWidth: '100%',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  loadingIndicator: {
    marginTop: '2rem',
    fontSize: '1.2rem',
    color: '#666',
  },
};

const Imagegenerate: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const generateImage = async () => {
    setLoading(true);
    setImageUrl('');

    const structuredPrompt = `High-resolution, studio-quality digital render of: ${prompt}. Style: Fashion photography. Lighting: soft and even. Background: neutral. Focus: full outfit only, no model, clean presentation.`;

    try {
      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer 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`,
        },
        body: JSON.stringify({
          model: 'dall-e-3',
          prompt: structuredPrompt,
          n: 1,
          size: '1024x1024',
          response_format: 'url',
          style: 'vivid', // Optional: 'vivid' or 'natural' (vivid gives more vibrant colors)
          quality: 'hd', // Optional: for higher detail (note: may increase cost)
        }),
      });

      const data = await response.json();

      if (response.ok && data.data && data.data.length > 0) {
        setImageUrl(data.data[0].url);
      } else {
        // eslint-disable-next-line no-console
        console.error('API Error:', data);
        throw new Error(data.error?.message || 'Image generation failed');
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error generating image:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer} className="image-generator-container">
      <div style={styles.container}>
        <h2 style={styles.title}>🎨 AI Image Generator</h2>

        <div style={styles.inputContainer}>
          <input
            style={styles.input}
            type="text"
            placeholder="Describe the image you want to generate..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />

          <button
            onClick={generateImage}
            disabled={loading || !prompt}
            style={{
              ...styles.button,
              ...(loading || !prompt ? styles.buttonDisabled : {}),
            }}
          >
            {loading ? 'Generating...' : 'Generate Image'}
          </button>
        </div>

        {loading && (
          <div style={styles.loadingIndicator}>✨ Creating your image... Please wait.</div>
        )}

        {imageUrl && (
          <div style={styles.imageContainer}>
            <img
              src={imageUrl}
              alt="Generated"
              style={styles.image}
              className="w-[32rem] aspect-auto"
            />

            <div className="w-full flex gap-3">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = imageUrl;
                  link.setAttribute('download', 'ai-fashion-dress'); // Filename
                  link.setAttribute('target', '_blank'); // Opens in new tab (safer for CORS)
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={styles.button}
              >
                ⬇️ Download
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(imageUrl);
                  alert('Image URL copied!');
                }}
                style={styles.button}
              >
                Copy URL
              </button>

              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Check out this AI-generated image!',
                      url: imageUrl,
                    });
                  } else {
                    alert('Sharing not supported on this device.');
                  }
                }}
                style={styles.button}
              >
                Share
              </button>

              <button
                onClick={() => {
                  setImageUrl('');
                  setPrompt('');
                }}
                style={{ ...styles.button, backgroundColor: '#ff6b6b' }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Imagegenerate;
